<template>
  <v-dialog :value="value" max-width="600" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="primary white--text d-flex justify-space-between">
        <span>Guía de secciones de formulario</span>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6 pb-2" style="min-height: 260px">
        <!-- Slide 1 -->
        <div v-if="currentSlide === 0">
          <div class="d-flex align-center mb-4">
            <v-icon size="48" color="primary" class="mr-4">mdi-view-split-vertical</v-icon>
            <div>
              <p class="subtitle-1 font-weight-bold mb-1">¿Qué son las secciones?</p>
              <p class="body-2 grey--text text--darken-1">
                Las secciones te permiten organizar los campos de tu formulario en grupos temáticos.
                Cuando el usuario llena el formulario, verá un asistente paso a paso — un paso por cada sección — en lugar de una lista larga de campos.
              </p>
            </div>
          </div>
          <v-alert type="info" outlined dense>
            Los formularios sin secciones siguen funcionando exactamente igual que antes.
          </v-alert>
        </div>

        <!-- Slide 2 -->
        <div v-if="currentSlide === 1">
          <div class="d-flex align-center mb-4">
            <v-icon size="48" color="primary" class="mr-4">mdi-table-row-plus-after</v-icon>
            <div>
              <p class="subtitle-1 font-weight-bold mb-1">Crear una sección</p>
              <p class="body-2 grey--text text--darken-1">
                Haz clic en el botón <strong>"Agregar sección"</strong> para crear un nuevo grupo.
                La sección aparecerá como una tarjeta editable en la parte superior de la página.
              </p>
            </div>
          </div>
          <v-card outlined class="pa-3 d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-table-row-plus-after</v-icon>
            <span class="body-2">Agregar sección</span>
          </v-card>
          <p class="caption grey--text mt-3">Puedes crear tantas secciones como necesites. Cada sección tendrá un nombre que el usuario verá en el paso correspondiente.</p>
        </div>

        <!-- Slide 3 -->
        <div v-if="currentSlide === 2">
          <div class="d-flex align-center mb-4">
            <v-icon size="48" color="primary" class="mr-4">mdi-link-variant</v-icon>
            <div>
              <p class="subtitle-1 font-weight-bold mb-1">Asignar campos a una sección</p>
              <p class="body-2 grey--text text--darken-1">
                Al <strong>crear o editar un campo</strong>, encontrarás un selector llamado <strong>"Sección"</strong>.
                Selecciona en cuál de tus secciones debe aparecer ese campo.
              </p>
            </div>
          </div>
          <v-card outlined class="pa-3">
            <v-select
              :items="['Datos personales', 'Información profesional', 'Sin sección']"
              value="Datos personales"
              label="Sección"
              dense
              filled
              rounded
              hide-details
              readonly
            />
          </v-card>
          <p class="caption grey--text mt-3">Los campos sin sección asignada aparecerán en un grupo llamado <strong>"Campos sin sección"</strong> al inicio del formulario.</p>
        </div>

        <!-- Slide 4 -->
        <div v-if="currentSlide === 3">
          <div class="d-flex align-center mb-4">
            <v-icon size="48" color="primary" class="mr-4">mdi-sort-numeric-ascending</v-icon>
            <div>
              <p class="subtitle-1 font-weight-bold mb-1">Renombrar y eliminar secciones</p>
              <p class="body-2 grey--text text--darken-1">
                Desde las tarjetas de sección puedes <strong>renombrar</strong> la sección en cualquier momento y <strong>eliminarla</strong>.
                Si eliminas una sección, los campos que le pertenecían pasarán al grupo "Campos sin sección"; no se eliminan.
              </p>
            </div>
          </div>
          <v-card outlined class="pa-3">
            <v-row align="center" no-gutters>
              <v-col>
                <v-text-field value="Datos personales" label="Nombre de la sección" dense filled rounded hide-details readonly />
              </v-col>
              <v-col cols="auto" class="ml-2">
                <v-btn icon small color="green"><v-icon small>mdi-content-save-outline</v-icon></v-btn>
                <v-btn icon small color="red"><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
              </v-col>
            </v-row>
          </v-card>
        </div>

        <!-- Slide 5 -->
        <div v-if="currentSlide === 4">
          <div class="d-flex align-center mb-4">
            <v-icon size="48" color="primary" class="mr-4">mdi-view-sequential-outline</v-icon>
            <div>
              <p class="subtitle-1 font-weight-bold mb-1">Vista del usuario final</p>
              <p class="body-2 grey--text text--darken-1">
                Cuando un usuario llene el formulario verá un <strong>asistente paso a paso</strong> — un paso por sección.
                Deberá completar cada paso antes de avanzar al siguiente.
              </p>
            </div>
          </div>
          <v-stepper :value="2" alt-labels class="elevation-0">
            <v-stepper-header style="box-shadow: none; border: 1px solid #e0e0e0; border-radius: 8px;">
              <v-stepper-step step="1" complete>Datos personales</v-stepper-step>
              <v-divider />
              <v-stepper-step step="2">Información profesional</v-stepper-step>
              <v-divider />
              <v-stepper-step step="3">Documentos</v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </div>
      </v-card-text>

      <!-- Dots progress -->
      <v-card-text class="pt-0 d-flex justify-center">
        <v-btn
          v-for="i in totalSlides"
          :key="i"
          icon
          x-small
          :color="currentSlide === i - 1 ? 'primary' : 'grey lighten-1'"
          @click="currentSlide = i - 1"
        >
          <v-icon x-small>mdi-circle</v-icon>
        </v-btn>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-btn text :disabled="currentSlide === 0" @click="currentSlide--">
          Anterior
        </v-btn>
        <v-spacer />
        <v-btn v-if="currentSlide < totalSlides - 1" color="primary" @click="currentSlide++">
          Siguiente
        </v-btn>
        <v-btn v-else color="primary" @click="close">
          Entendido
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SectionsTutorialDialog',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    currentSlide: 0,
    totalSlides: 5
  }),
  watch: {
    value(newVal) {
      if (newVal) {
        this.currentSlide = 0;
      }
    }
  },
  methods: {
    close() {
      localStorage.setItem('sections_tutorial_seen', 'true');
      this.$emit('input', false);
    }
  }
};
</script>
